// FIXED, audited catalog of classic experiences and where they live in the new
// dashboard. Every item is embedded in-place (same origin, same auth cookies) via
// its original URL; `embed=1` tells the classic Dashboard to hide its own sidebar.
// Server never supplies routes — this file is the single source of truth.
import {
  Layers, Sprout, Store, FlaskConical, TrendingUp, Globe2, Landmark, Building2, FileSpreadsheet,
  DollarSign, BarChart3, Activity, ShieldAlert, Shield, Settings, User, CreditCard, Users, Bell,
  Cpu, GitBranch, Workflow, Boxes, Wrench, Waypoints, Zap, Library,
} from "lucide-react";

const dash = (tab) => `/dashboard?tab=${tab}&embed=1`;

export const CATALOG = {
  optimization: [
    { id: "haven", label: "HAVEN", icon: Layers, desc: "Contract & rights optimization swarm and the HAVEN portfolio lab.",
      defaultItem: "haven-swarm",
      items: [
        { id: "haven-swarm", label: "HAVEN Swarm", path: "/haven/swarm", desc: "Live multi-agent swarm over the HAVEN pipeline." },
        { id: "haven-portfolio-lab", label: "HAVEN Portfolio Lab", path: "/haven/portfolio-lab", desc: "Scenario lab for HAVEN portfolios." },
      ] },
    { id: "decision-graph", label: "Decision Graph — XIUS & GRIFFENDO", icon: Waypoints,
      desc: "Live cinematic Decision Graph runs: XIUS roaming capacity and GRIFFENDO security response.",
      items: [{ id: "decision-graph-live", label: "Decision Graph", path: "/app/decision-graph", internal: true, desc: "XIUS & GRIFFENDO live runs, snapshots and autoplay demo." }] },
    { id: "harvest", label: "HARVEST", icon: Sprout, desc: "Roaming-revenue workbook optimization, live.",
      items: [{ id: "harvest-live", label: "HARVEST Live", path: "/harvest/live", desc: "Live HARVEST workspace." }] },
    { id: "retail", label: "Retail", icon: Store, desc: "Retail swarm constellation.",
      items: [{ id: "retail-swarm", label: "Retail Swarm", path: "/swarm", desc: "Agent swarm constellation — retail mode." }] },
    { id: "pharmaceutical", label: "Pharmaceutical", icon: FlaskConical, desc: "Pharmaceutical swarm (J&J mode).",
      items: [{ id: "pharma-swarm", label: "Pharmaceutical Swarm", path: "/swarm?mode=jnj", desc: "Agent swarm constellation — pharmaceutical mode." }] },
    { id: "brand-management", label: "Brand Management", icon: TrendingUp, desc: "Sales & brand-management swarm.",
      items: [{ id: "sales-swarm", label: "Brand Management Swarm", path: dash("sales-swarm"), desc: "Sales swarm from the classic dashboard." }] },
    { id: "licensing", label: "Licensing", icon: Globe2, desc: "Rightsline licensing swarm.",
      items: [{ id: "rightsline", label: "Rightsline", path: "/rightsline", desc: "Licensing optimization dashboard." }] },
    { id: "grants", label: "Grants", icon: Landmark, desc: "CSH grants portfolio swarm.",
      items: [{ id: "csh-swarm", label: "CSH Grants Swarm", path: "/csh/swarm", desc: "Grants swarm." }] },
    { id: "private-equity", label: "Private Equity", icon: Building2, desc: "Portfolio simulators I and II.",
      items: [
        { id: "portfolio", label: "Private Equity I", path: "/portfolio", desc: "Portfolio simulator." },
        { id: "portfolio-hf", label: "Private Equity II", path: "/portfolio/hf", desc: "H&F portfolio simulator." },
      ] },
    { id: "workflow-optimizer", label: "Workflow Optimizer", icon: Zap, desc: "Optimize My Workflow — the classic home: one-click optimization, agent health, recent activity.",
      items: [{ id: "workflow-optimizer", label: "Workflow Optimizer", path: dash("optimize"), desc: "Classic optimizer home." }] },
    { id: "deal-analysis", label: "Deal Analysis", icon: FileSpreadsheet, desc: "Deal analysis workbench.",
      items: [{ id: "deal-analysis", label: "Deal Analysis", path: "/deal-analysis", desc: "Deal analysis." }] },
    // HAVEN Deal (Phase 2) — flag-gated; only appears when REACT_APP_HAVEN_DEAL_ENABLED is true.
    ...(String(process.env.REACT_APP_HAVEN_DEAL_ENABLED || "").toLowerCase() === "true" ? [
      { id: "haven-deal", label: "HAVEN Deal", icon: FlaskConical,
        desc: "Phase 2 quantum-native asset-acquisition duplicate (development).",
        items: [{ id: "haven-deal", label: "HAVEN Deal", path: "/app/optimization/haven-deal",
                  internal: true, desc: "Metro × window deal instances (classical verdict pending)." }] },
    ] : []),
  ],
  reports: [
    { id: "savings", label: "Savings", icon: DollarSign, desc: "Realized savings across all optimizations.",
      items: [{ id: "savings", label: "Savings", path: dash("analytics"), desc: "Savings analytics." }] },
    { id: "overview", label: "Overview", icon: BarChart3, desc: "Classic overview dashboard.",
      items: [{ id: "overview", label: "Overview", path: dash("overview"), desc: "Overview." }] },
    { id: "intelligence", label: "Intelligence", icon: Activity, desc: "Cross-vertical intelligence.",
      items: [{ id: "intelligence", label: "Intelligence", path: dash("cross-vertical"), desc: "Cross-vertical intelligence." }] },
    { id: "spend-protection", label: "Spend Protection", icon: ShieldAlert, desc: "Spend guardrails and alerts.",
      items: [{ id: "spend-protection", label: "Spend Protection", path: "/spend-protection", desc: "Spend protection." }] },
    { id: "provider-rates", label: "Provider Rates", icon: DollarSign, desc: "Provider rate cards.",
      items: [{ id: "provider-rates", label: "Provider Rates", path: "/providers", desc: "Provider rates." }] },
    { id: "knowledge-explorer", label: "Knowledge Explorer", icon: Library, desc: "Explore the knowledge base across individual, team and organization scopes.",
      items: [{ id: "knowledge-explorer", label: "Knowledge Explorer", path: dash("kb"), desc: "Knowledge explorer." }] },
    { id: "status", label: "Status", icon: Shield, desc: "Engine and provider status.",
      items: [{ id: "status", label: "Status", path: dash("status"), desc: "Status." }] },
  ],
  settings: [
    { id: "settings", label: "Settings", icon: Settings, desc: "Account and workspace settings.",
      items: [{ id: "settings", label: "Settings", path: "/settings", desc: "Settings." }] },
    { id: "profile", label: "Profile", icon: User, desc: "Your profile.",
      items: [{ id: "profile", label: "Profile", path: dash("profile"), desc: "Profile." }] },
    { id: "billing", label: "Plan & Billing", icon: CreditCard, desc: "Plan, invoices and credits.",
      items: [{ id: "billing", label: "Plan & Billing", path: dash("billing"), desc: "Billing." }] },
    { id: "team", label: "Team", icon: Users, desc: "Members and invitations.",
      items: [{ id: "team", label: "Team", path: dash("team-mgmt"), desc: "Team management." }] },
    { id: "usage", label: "Usage", icon: BarChart3, desc: "Usage and quotas.",
      items: [{ id: "usage", label: "Usage", path: dash("usage"), desc: "Usage." }] },
    { id: "scope-alerts", label: "Scope alerts", icon: Bell, desc: "Scope alert preferences.",
      items: [{ id: "scope-alerts", label: "Scope alerts", path: dash("scope-alerts"), desc: "Scope alerts." }] },
  ],
  "platform-admin": [
    { id: "platform-admin", label: "Platform Admin", icon: ShieldAlert, desc: "Root console — tenants, keys, platform controls.",
      items: [{ id: "platform-admin", label: "Platform Admin console", path: "/platform-admin", desc: "Root console." }] },
    { id: "tenant-admin", label: "Tenant Admin", icon: Shield, desc: "Classic tenant administration.",
      items: [{ id: "tenant-admin", label: "Tenant Admin", path: dash("tenant-admin"), desc: "Tenant admin." }] },
    { id: "engine", label: "Engine", icon: Cpu, desc: "Optimizer, model catalog, providers, agent swarm.",
      items: [
        { id: "optimizer", label: "Optimizer", path: dash("optimization-engine"), desc: "Optimization engine." },
        { id: "model-catalog", label: "Model Catalog", path: dash("model-catalog"), desc: "Model catalog." },
        { id: "provider-mgmt", label: "Providers", path: dash("provider-mgmt"), desc: "Provider management." },
        { id: "agent-swarm", label: "Agent Swarm", path: dash("swarm"), desc: "Agent swarm." },
      ] },
    { id: "workflows", label: "Workflows", icon: Workflow, desc: "Workflow definitions.",
      items: [{ id: "workflows", label: "Workflows", path: dash("workflows"), desc: "Workflows." }] },
    { id: "governance", label: "Governance", icon: Boxes, desc: "Module registry and tenant approvals (new dashboard areas).",
      items: [
        { id: "modules", label: "Modules", path: "/app/modules", internal: true, desc: "Module registry." },
        { id: "admin", label: "Admin", path: "/app/admin", internal: true, desc: "Tenant approvals & policies." },
      ] },
    { id: "routing-interactions", label: "Routing Interactions", icon: Waypoints, desc: "C8 traffic-interaction snapshot, R4 policy diagnostics and frozen R4b bench bundles.",
      items: [{ id: "routing-interactions", label: "Routing Interactions", path: "/app/platform-admin/routing-interactions", internal: true, desc: "Interaction aggregator report." }] },
    { id: "dev", label: "Developer", icon: Wrench, desc: "Portals, playground, API docs.",
      items: [
        { id: "haven-portal", label: "HAVEN Portal", path: dash("haven-portal") },
        { id: "harvest-portal", label: "HARVEST Portal", path: dash("harvest-portal") },
        { id: "gateway", label: "Gateway Playground", path: dash("models") },
        { id: "api-portal", label: "API Portal", path: dash("docs") },
      { id: "code-examples", label: "Code Examples", path: dash("examples") },
      ] },
  ],
};

export const ICON_FALLBACK = GitBranch;

export function findFamily(areaKey, id) {
  return (CATALOG[areaKey] || []).find((f) => f.id === id) || null;
}

export function findItem(areaKey, id) {
  for (const f of CATALOG[areaKey] || []) {
    const it = f.items.find((i) => i.id === id);
    if (it) return { family: f, item: it };
  }
  return null;
}

// Card click target: a family with a default item (or a single item) opens that item
// directly; an internal single item navigates to its in-app route.
export function familyTarget(areaKey, family) {
  const direct = family.defaultItem
    ? family.items.find((i) => i.id === family.defaultItem)
    : (family.items.length === 1 ? family.items[0] : null);
  if (!direct) return `/app/${areaKey}/${family.id}`;
  return direct.internal ? direct.path : `/app/${areaKey}/${direct.id}`;
}

// Sibling experiences shown as tabs inside an embedded item (families with a default item).
export function familyTabs(family) {
  return family.defaultItem && family.items.length > 1 ? family.items : [];
}
